// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// The voice-intake screen: one calm orb, the live transcript, and a small set of persistent
// controls. The orb's colour and motion ARE the state signal — there is no visible label;
// the phase is announced only to assistive tech via a visually-hidden live region. Every
// visual state maps to a real transport signal via orbStateFor() — the overlay itself holds
// no timers or derived phases.

import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Mic, MicOff, Keyboard, X, RefreshCw } from 'lucide-react'
import { Logo } from '@/components/Logo'
import { Branding } from '@qh/design-tokens/branding'
import type { VoiceState } from '@/intake/types'
import { VoiceOrb, type OrbState } from '@/components/VoiceOrb'

/** Voice states the overlay renders. Permission + idle/unsupported are handled elsewhere. */
export type OverlayVoiceState = Extract<
  VoiceState,
  'connecting' | 'reconnecting' | 'patient-listening' | 'assistant-thinking' | 'assistant-speaking' | 'ended' | 'error'
>

const OVERLAY_STATES: OverlayVoiceState[] = [
  'connecting',
  'reconnecting',
  'patient-listening',
  'assistant-thinking',
  'assistant-speaking',
  'ended',
  'error',
]

/** True when the orb overlay owns the screen for this voice state. */
export function isOverlayVoiceState(state: VoiceState): state is OverlayVoiceState {
  return (OVERLAY_STATES as VoiceState[]).includes(state)
}

interface VoiceModeOverlayProps {
  state: OverlayVoiceState
  muted: boolean
  transcript: string
  caption: string
  onMute: () => void
  onSwitchToText: () => void
  onEnd: () => void
  onRetry: () => void
}

/** Pure map from the transport state (+ mute) to the orb's visual state. */
export function orbStateFor(state: OverlayVoiceState, muted: boolean): OrbState {
  switch (state) {
    case 'connecting':
    case 'reconnecting':
      return 'connecting'
    case 'assistant-speaking':
      return 'speaking'
    case 'assistant-thinking':
      return 'thinking'
    case 'ended':
      return 'ended'
    case 'error':
      return 'error'
    case 'patient-listening':
      return muted ? 'muted' : 'listening'
  }
}

function captionKey(orb: OrbState): string {
  return {
    connecting: 'voice.captionConnecting',
    listening: 'voice.captionListening',
    thinking: 'voice.captionThinking',
    speaking: 'voice.captionSpeaking',
    muted: 'voice.captionMuted',
    error: 'voice.captionError',
    ended: 'voice.captionEnded',
  }[orb]
}

export function VoiceModeOverlay({
  state,
  muted,
  transcript,
  caption,
  onMute,
  onSwitchToText,
  onEnd,
  onRetry,
}: VoiceModeOverlayProps) {
  const { t } = useTranslation()
  const orb = orbStateFor(state, muted)
  const isSpeaking = orb === 'speaking'
  const inCall = orb === 'listening' || orb === 'thinking' || orb === 'speaking' || orb === 'muted'
  const stateCaption = state === 'reconnecting' ? t('voice.captionReconnecting') : t(captionKey(orb))

  // Live transcript view is disabled for a calmer voice UI; flip to true to restore.
  const SHOW_LIVE_TRANSCRIPT = false
  // Show the assistant's caption while it speaks; otherwise the patient's own words.
  const liveText = isSpeaking ? caption : transcript
  const liveLabel = isSpeaking ? t('voice.liveCaption') : t('voice.liveTranscript')

  return (
    <div className="fixed inset-0 z-40 flex flex-col animate-overlay-in" style={{ background: '#011829' }}>
      {/* Ambient depth */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#006EF9]/6 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-[#02C7C3]/6 blur-[100px] pointer-events-none" />

      {/* Quiet brand mark */}
      <div className="flex-shrink-0 flex justify-center pt-12 pb-2">
        <div style={{ filter: 'brightness(0) invert(1)', opacity: 0.35 }}>
          <Logo variant="mark" className="h-6 w-auto" aria-hidden />
        </div>
      </div>

      {/* Orb is the focal point; the transcript sits beneath it. No visible state label —
          the orb's colour and motion carry the state. */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 gap-8 min-h-0">
        <VoiceOrb state={orb} />

        {/* Visible "getting ready" label while connecting — the orb alone doesn't tell the
            patient the assistant is still coming; every other state is self-evident. */}
        {orb === 'connecting' && (
          <p aria-hidden="true" className="text-sm text-white/60 animate-caption-in -mt-2">{stateCaption}</p>
        )}

        {/* State announcement for assistive tech only — visually hidden, never on screen. */}
        <p role="status" aria-live="polite" className="sr-only">
          {stateCaption}
        </p>

        {SHOW_LIVE_TRANSCRIPT && inCall && liveText ? (
          <div className="w-full max-w-sm animate-caption-in">
            <p className="text-[10px] font-semibold text-white/30 uppercase tracking-widest mb-2">{liveLabel}</p>
            <div
              className="rounded-2xl px-4 py-3.5 max-h-28 overflow-y-auto scrollbar-hide"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(12px)' }}
            >
              <p className="text-[14px] text-white/80 leading-relaxed">{liveText}</p>
            </div>
          </div>
        ) : (
          <div className="w-full max-w-sm h-16" />
        )}

        {orb === 'error' && (
          <p className="text-[13px] text-white/50 text-center max-w-xs leading-relaxed -mt-2">{t('voice.errorBody')}</p>
        )}
      </div>

      {/* Controls — grouped by phase; the in-call set is always the same three, no flicker. */}
      <div className="flex-shrink-0 px-5 pb-10 pt-4">
        <div
          className="rounded-2xl p-1 flex gap-1"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}
        >
          {orb === 'connecting' && (
            <ActionButton onClick={onSwitchToText} icon={<X className="w-4 h-4" />} label={t('voice.cancel')} />
          )}

          {inCall && (
            <>
              <ActionButton
                onClick={onMute}
                icon={muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                label={muted ? t('voice.unmuteButton') : t('voice.muteButton')}
                pressed={muted}
              />
              <ActionButton onClick={onSwitchToText} icon={<Keyboard className="w-4 h-4" />} label={t('voice.switchToText')} />
              <ActionButton onClick={onEnd} icon={<X className="w-4 h-4" />} label={t('voice.endButton')} destructive />
            </>
          )}

          {orb === 'error' && (
            <>
              <ActionButton onClick={onRetry} icon={<RefreshCw className="w-4 h-4" />} label={t('voice.retry')} />
              <ActionButton onClick={onSwitchToText} icon={<Keyboard className="w-4 h-4" />} label={t('voice.continueByText')} />
            </>
          )}

          {orb === 'ended' && (
            <ActionButton onClick={onSwitchToText} icon={<Keyboard className="w-4 h-4" />} label={t('voice.continueByText')} />
          )}
        </div>
        <Branding variant="inverse" className="mt-4 text-[11px]" />
      </div>
    </div>
  )
}

interface ActionButtonProps {
  onClick: () => void
  icon: ReactNode
  label: string
  destructive?: boolean
  pressed?: boolean
}

function ActionButton({ onClick, icon, label, destructive = false, pressed = false }: ActionButtonProps) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      aria-pressed={pressed}
      className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-3 text-[13px] font-medium transition-all duration-150 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 ${
        destructive
          ? 'bg-red-500/15 border border-red-500/25 text-red-300 hover:bg-red-500/25'
          : pressed
            ? 'bg-white/20 border border-white/25 text-white'
            : 'bg-white/6 border border-white/10 text-white/70 hover:bg-white/12 hover:text-white'
      }`}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  )
}
